import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  PushpinOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { revalidateNextTag } from '@/lib/revalidate'
import { formatCST, pickerToUTCISO, toCSTDayjs } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/announcements')({
  component: AnnouncementsPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type Severity = 'info' | 'warning' | 'critical'
type DisplayMode = 'banner' | 'popup'
type Surface = 'web' | 'client'

interface AnnouncementRow {
  id: string
  title: string
  body: string | null
  severity: Severity
  display_mode: DisplayMode
  surfaces: Surface[]
  action_text: string | null
  action_url: string | null
  is_published: boolean
  pinned: boolean
  priority: number
  starts_at: string | null
  ends_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

type PublishedFilter = 'published' | 'draft' | 'all'

const SEVERITY_OPTIONS: Array<{
  value: Severity
  label: string
  color: string
}> = [
  { value: 'info', label: '信息', color: 'blue' },
  { value: 'warning', label: '警告', color: 'orange' },
  { value: 'critical', label: '紧急', color: 'red' },
]

const DISPLAY_OPTIONS: Array<{ value: DisplayMode; label: string }> = [
  { value: 'banner', label: '横幅 Banner' },
  { value: 'popup', label: '弹窗 Popup' },
]

const SURFACE_OPTIONS: Array<{ value: Surface; label: string }> = [
  { value: 'web', label: 'Web 网页版' },
  { value: 'client', label: 'Client 客户端' },
]

function severityMeta(s: Severity) {
  return SEVERITY_OPTIONS.find((o) => o.value === s)!
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function AnnouncementsPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()

  const [published, setPublished] = useState<PublishedFilter>('all')
  const [editing, setEditing] = useState<AnnouncementRow | 'new' | null>(null)

  const listQuery = useQuery({
    queryKey: ['announcements'],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // admin.list_announcements 绕过 RLS，能看到草稿 / 过期 / 撤回

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_announcements', { p_limit: 200, p_offset: 0 })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        rows?: AnnouncementRow[]
        total?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.rows ?? []
    },
  })

  const filteredRows = useMemo(() => {
    const rows = listQuery.data ?? []
    if (published === 'all') return rows
    if (published === 'published') return rows.filter((r) => r.is_published)
    return rows.filter((r) => !r.is_published)
  }, [listQuery.data, published])

  const publishMutation = useMutation({
    mutationFn: async (params: { id: string; isPublished: boolean }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_announcement_published', {
          p_id: params.id,
          p_is_published: params.isPublished,
          p_reason: null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (_, vars) => {
      message.success(vars.isPublished ? '已发布' : '已撤回')
      qc.invalidateQueries({ queryKey: ['announcements'] })
      void revalidateNextTag('announcements')
    },
    onError: (e: Error) => message.error(e.message || '操作失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_announcement', { p_id: id, p_reason: null })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: () => {
      message.success('已删除')
      qc.invalidateQueries({ queryKey: ['announcements'] })
      void revalidateNextTag('announcements')
    },
    onError: (e: Error) => message.error(e.message || '删除失败'),
  })

  const columns: ColumnsType<AnnouncementRow> = useMemo(
    () => [
      {
        title: '公告',
        render: (_: unknown, row) => (
          <Space size={6} wrap>
            {row.pinned ? (
              <Tooltip title="置顶">
                <PushpinOutlined style={{ color: '#fa8c16' }} />
              </Tooltip>
            ) : null}
            <Typography.Text strong style={{ fontSize: 13 }}>
              {row.title}
            </Typography.Text>
            {row.action_text ? (
              <Tooltip title={row.action_url ?? ''}>
                <Tag color="geekblue">{row.action_text}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
      {
        title: '严重程度',
        dataIndex: 'severity',
        width: 90,
        render: (s: Severity) => {
          const m = severityMeta(s)
          return <Tag color={m.color}>{m.label}</Tag>
        },
      },
      {
        title: '展示',
        dataIndex: 'display_mode',
        width: 90,
        render: (d: DisplayMode) => (
          <Tag>{d === 'popup' ? '弹窗' : '横幅'}</Tag>
        ),
      },
      {
        title: '平台',
        dataIndex: 'surfaces',
        width: 130,
        render: (arr: Surface[]) => (
          <Space size={4} wrap>
            {arr.map((s) => (
              <Tag key={s} color={s === 'web' ? 'cyan' : 'purple'}>
                {s === 'web' ? 'Web' : 'Client'}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: '生效时段',
        width: 200,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 11 }}>
              {row.starts_at ? formatCST(row.starts_at, 'MM-DD HH:mm') : '立即'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              →{' '}
              {row.ends_at ? formatCST(row.ends_at, 'MM-DD HH:mm') : '永不过期'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '优先级',
        width: 80,
        render: (_: unknown, row) => (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {row.pinned ? '⬆ ' : ''}
            {row.priority}
          </Typography.Text>
        ),
      },
      {
        title: '发布',
        dataIndex: 'is_published',
        width: 90,
        render: (b: boolean) => (
          <Tag color={b ? 'success' : 'default'}>{b ? '已发布' : '草稿'}</Tag>
        ),
      },
      {
        title: '更新于',
        dataIndex: 'updated_at',
        width: 130,
        render: (t: string) => (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {formatCST(t, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 200,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                setEditing(row)
              }}
            >
              编辑
            </Button>
            <Popconfirm
              title={row.is_published ? '撤回该公告？' : '发布该公告？'}
              description={
                row.is_published
                  ? 'C 端将不再看到该公告。'
                  : 'C 端将立即（或按 starts_at）看到。'
              }
              onConfirm={() =>
                publishMutation.mutate({
                  id: row.id,
                  isPublished: !row.is_published,
                })
              }
              okText={row.is_published ? '撤回' : '发布'}
              cancelText="取消"
              okButtonProps={{ danger: row.is_published }}
            >
              <Button
                type="link"
                size="small"
                danger={row.is_published}
                onClick={(e) => e.stopPropagation()}
              >
                {row.is_published ? '撤回' : '发布'}
              </Button>
            </Popconfirm>
            <Popconfirm
              title="删除该公告？"
              description="硬删除，无法恢复。写动作仍会留在审计日志里。"
              onConfirm={() => deleteMutation.mutate(row.id)}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [publishMutation, deleteMutation],
  )

  return (
    <>
      <Card
        title="公告"
        extra={
          <Space size={8}>
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              {isMobile ? '新建' : '新建公告'}
            </Button>
          </Space>
        }
      >
        <div
          className="admin-scroll-rail moderation-tab-rail"
          style={{ marginBottom: 16 }}
        >
          <Segmented<PublishedFilter>
            value={published}
            onChange={(v) => setPublished(v)}
            options={[
              { label: '全部', value: 'all' },
              { label: '已发布', value: 'published' },
              { label: '草稿', value: 'draft' },
            ]}
          />
        </div>

        <div className="admin-table-wrap">
          <Table<AnnouncementRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={filteredRows}
            onRow={(row) => ({
              onClick: () => setEditing(row),
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 1100 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </div>

        <AdminMobileList
          rows={filteredRows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              onClick={() => setEditing(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setEditing(row)
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  {row.pinned ? (
                    <PushpinOutlined style={{ color: '#fa8c16' }} />
                  ) : null}{' '}
                  <Typography.Text>{row.title}</Typography.Text>
                </div>
                <Tag color={row.is_published ? 'success' : 'default'}>
                  {row.is_published ? '已发布' : '草稿'}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                <Tag color={severityMeta(row.severity).color}>
                  {severityMeta(row.severity).label}
                </Tag>
                <Tag>{row.display_mode === 'popup' ? '弹窗' : '横幅'}</Tag>
                {row.surfaces.map((surface) => (
                  <Tag
                    key={surface}
                    color={surface === 'web' ? 'cyan' : 'purple'}
                  >
                    {surface === 'web' ? 'Web' : 'Client'}
                  </Tag>
                ))}
                {row.action_text ? (
                  <Tag color="geekblue">{row.action_text}</Tag>
                ) : null}
              </div>
              {row.body ? (
                <div className="admin-mobile-record__body">
                  <Typography.Paragraph ellipsis={{ rows: 2 }}>
                    {row.body}
                  </Typography.Paragraph>
                </div>
              ) : null}
              <div className="admin-mobile-record__meta">
                <span>
                  {row.starts_at
                    ? formatCST(row.starts_at, 'MM-DD HH:mm')
                    : '立即'}
                  {' → '}
                  {row.ends_at
                    ? formatCST(row.ends_at, 'MM-DD HH:mm')
                    : '永不过期'}
                </span>
                <span>优先级 {row.priority}</span>
                <span>更新 {formatCST(row.updated_at, 'MM-DD HH:mm')}</span>
              </div>
              {/* 卡片本身点开即编辑，这里只放发布/撤回 + 删除 */}
              <AdminRecordActions
                actions={[
                  {
                    key: 'delete',
                    label: '删除',
                    icon: <DeleteOutlined />,
                    danger: true,
                    loading:
                      deleteMutation.isPending &&
                      deleteMutation.variables === row.id,
                    confirm: {
                      title: '删除该公告？',
                      description:
                        '硬删除，无法恢复。写动作仍会留在审计日志里。',
                      okText: '删除',
                    },
                    onClick: () => deleteMutation.mutate(row.id),
                  },
                  {
                    key: 'publish',
                    label: row.is_published ? '撤回' : '发布',
                    danger: row.is_published,
                    loading:
                      publishMutation.isPending &&
                      publishMutation.variables?.id === row.id,
                    confirm: {
                      title: row.is_published ? '撤回该公告？' : '发布该公告？',
                      description: row.is_published
                        ? 'C 端将不再看到该公告。'
                        : 'C 端将立即（或按 starts_at）看到。',
                      okText: row.is_published ? '撤回' : '发布',
                    },
                    onClick: () =>
                      publishMutation.mutate({
                        id: row.id,
                        isPublished: !row.is_published,
                      }),
                  },
                ]}
              />
            </article>
          )}
        />
      </Card>

      <EditDrawer
        open={editing !== null}
        row={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          qc.invalidateQueries({ queryKey: ['announcements'] })
          void revalidateNextTag('announcements')
        }}
      />
    </>
  )
}

/* ── 编辑抽屉（新建/编辑共用）─────────────────────────────────── */

interface FormValues {
  title: string
  body: string | null
  severity: Severity
  display_mode: DisplayMode
  surfaces: Surface[]
  action_text: string | null
  action_url: string | null
  is_published: boolean
  pinned: boolean
  priority: number
  time_range: [Dayjs | null, Dayjs | null] | null
  reason: string
}

function EditDrawer({
  open,
  row,
  onClose,
  onSaved,
}: {
  open: boolean
  row: AnnouncementRow | null
  onClose: () => void
  onSaved: () => void
}) {
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [form] = Form.useForm<FormValues>()
  const isNew = !row

  useEffect(() => {
    if (!open) return
    if (row) {
      form.setFieldsValue({
        title: row.title,
        body: row.body ?? '',
        severity: row.severity,
        display_mode: row.display_mode,
        surfaces: row.surfaces,
        action_text: row.action_text ?? '',
        action_url: row.action_url ?? '',
        is_published: row.is_published,
        pinned: row.pinned,
        priority: row.priority,
        time_range:
          row.starts_at || row.ends_at
            ? [
                row.starts_at ? toCSTDayjs(row.starts_at) : null,
                row.ends_at ? toCSTDayjs(row.ends_at) : null,
              ]
            : null,
        reason: '',
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        severity: 'info',
        display_mode: 'banner',
        surfaces: ['web'],
        is_published: false,
        pinned: false,
        priority: 0,
      })
    }
  }, [open, row, form])

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const actionText = values.action_text?.trim() || null
      const actionUrl = values.action_url?.trim() || null
      // 客户端兜底：CTA 必须成对（RPC 也会再校验）
      if ((actionText === null) !== (actionUrl === null)) {
        throw new Error('action_text_url_must_pair')
      }
      const startsAt = pickerToUTCISO(values.time_range?.[0] ?? null)
      const endsAt = pickerToUTCISO(values.time_range?.[1] ?? null)

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('upsert_announcement', {
          p_id: row?.id ?? null,
          p_title: values.title.trim(),
          p_body: values.body?.trim() || null,
          p_severity: values.severity,
          p_display_mode: values.display_mode,
          p_surfaces: values.surfaces,
          p_action_text: actionText,
          p_action_url: actionUrl,
          p_is_published: values.is_published,
          p_pinned: values.pinned,
          p_priority: values.priority,
          p_starts_at: startsAt,
          p_ends_at: endsAt,
          p_reason: values.reason?.trim() || null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string; id?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: () => {
      message.success(isNew ? '已创建' : '已保存')
      onSaved()
    },
    onError: (e: Error) => {
      const msgMap: Record<string, string> = {
        title_required: '标题不能为空',
        surfaces_required: '至少选择一个平台',
        invalid_surface: '平台只能是 web / client',
        invalid_severity: '严重程度不合法',
        invalid_display_mode: '展示形式不合法',
        action_text_url_must_pair: 'CTA 文案和链接必须同时填或同时不填',
        invalid_time_range: '开始时间必须早于结束时间',
        not_found: '公告不存在（可能已被删除）',
        forbidden: '无权限',
      }
      message.error(msgMap[e.message] || e.message || '保存失败')
    },
  })

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 640}
      title={isNew ? '新建公告' : `编辑 · ${row.title}`}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={saveMutation.isPending}
            onClick={() => form.submit()}
          >
            {isNew ? '创建' : '保存'}
          </Button>
        </Space>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={(v) => saveMutation.mutate(v)}
      >
        <Form.Item
          label="标题"
          name="title"
          rules={[
            { required: true, message: '请输入标题' },
            { max: 120, message: '最长 120 字' },
          ]}
        >
          <Input placeholder="一句话主体，例如：服务器今晚 22:00 维护" />
        </Form.Item>

        <Form.Item label="正文（可选）" name="body">
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={2000}
            showCount
            placeholder="可以为空。Popup 模式下会作为详情正文展示。"
          />
        </Form.Item>

        <Space
          className="announcements-form-pair"
          style={{ display: 'flex', marginBottom: 0 }}
          align="start"
        >
          <Form.Item
            label="严重程度"
            name="severity"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Select
              options={SEVERITY_OPTIONS.map((o) => ({
                value: o.value,
                label: (
                  <Space>
                    <Tag color={o.color} style={{ marginRight: 0 }}>
                      {o.label}
                    </Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {o.value}
                    </Typography.Text>
                  </Space>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item
            label="展示形式"
            name="display_mode"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Select options={DISPLAY_OPTIONS} />
          </Form.Item>
        </Space>

        <Form.Item
          label="目标平台"
          name="surfaces"
          rules={[
            {
              validator: (_, v: Surface[] | undefined) =>
                v && v.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('至少选择一个平台')),
            },
          ]}
        >
          <Checkbox.Group options={SURFACE_OPTIONS} />
        </Form.Item>

        <Space
          className="announcements-form-pair"
          style={{ display: 'flex', marginBottom: 0 }}
        >
          <Form.Item
            label="CTA 文案（可选）"
            name="action_text"
            style={{ flex: 1 }}
          >
            <Input placeholder="如：查看详情 / 立即升级" maxLength={20} />
          </Form.Item>
          <Form.Item
            label="CTA 跳转 URL"
            name="action_url"
            style={{ flex: 1.4 }}
          >
            <Input placeholder="/profile#membership 或 https://..." />
          </Form.Item>
        </Space>

        <Form.Item
          label="生效时段（可选；不填即立即生效 / 永不过期）"
          name="time_range"
        >
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            placeholder={['开始时间', '结束时间']}
            allowEmpty={[true, true]}
          />
        </Form.Item>

        <Space size="large" wrap style={{ marginBottom: 16 }}>
          <Form.Item
            label="发布"
            name="is_published"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
            tooltip="未勾选 = 草稿，C 端看不到"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="置顶"
            name="pinned"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
            tooltip="置顶公告总在普通公告之上"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="优先级"
            name="priority"
            tooltip="同组内排序，越大越靠前"
            style={{ marginBottom: 0 }}
          >
            <InputNumber min={0} max={32767} style={{ width: 120 }} />
          </Form.Item>
        </Space>

        <Form.Item label="备注（可选，会写入审计日志）" name="reason">
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
            showCount
            placeholder="不填将记 admin_create_announcement / admin_update_announcement"
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
